import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle2, Phone, MapPin, Clock, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHero } from '../components/Sections';
import ContactForm from '../components/ContactForm';
import { serviceAreas as fbAreas, siteSettings as fbSettings, IMAGES } from '../data/mock';
import { useContent } from '../context/ContentContext';
import Seo from '../components/Seo';
import { businessLd, breadcrumbLd } from '../lib/seo';

const ServiceArea = () => {
  const { serviceAreas = fbAreas, siteSettings = fbSettings, _loaded } = useContent();
  const { slug } = useParams();
  const area = serviceAreas.find((a) => a.slug === slug);
  if (!area) {
    // Content may still be loading from the backend; don't redirect prematurely.
    if (!_loaded) {
      return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>;
    }
    return <Navigate to="/service-areas/cortez-co" replace />;
  }
  const loc = area.state ? `${area.city}, ${area.state}` : area.city;

  return (
    <div>
      <Seo
        title={`${area.title} | HVAC Repair & Installation | Comfort Air Mechanical`}
        description={`${area.intro} Call ${siteSettings.phone} for fast, licensed HVAC service in ${loc}.`.slice(0, 300)}
        image={IMAGES.heroFamily}
        jsonLd={[
          businessLd(siteSettings, { image: IMAGES.heroFamily, areaServed: [area] }),
          breadcrumbLd([{ name: 'Home', path: '/' }, { name: loc, path: `/service-areas/${area.slug}` }])
        ]}
      />
      <PageHero title={area.title} subtitle={area.subtitle} crumb={loc} />

      <section className="section-pad bg-white">
        <div className="container-c grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          <div className="lg:col-span-2">
            <p className="text-xl text-brand-bluedark font-display font-semibold leading-snug">{area.intro}</p>
            {area.body.map((p, i) => (
              <p key={i} className="text-slate-600 leading-relaxed mt-4">{p}</p>
            ))}

            <div className="mt-8 flex items-start gap-3 bg-brand-bluelight rounded-xl p-5">
              <MapPin className="text-brand-blue shrink-0 mt-0.5" size={22} />
              <p className="text-slate-700 leading-relaxed">{area.landmarks}</p>
            </div>

            <h2 className="font-display font-extrabold text-2xl text-brand-bluedark mt-10 mb-5 flex items-center gap-2">
              <Wrench className="text-brand-green" /> HVAC Services We Offer in {area.city}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {area.services.map((s) => (
                <div key={s} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-100 shadow-sm hover-lift">
                  <CheckCircle2 className="text-brand-green shrink-0" size={20} />
                  <span className="font-medium text-brand-bluedark">{s}</span>
                </div>
              ))}
            </div>

            <a href={`tel:${siteSettings.phoneRaw}`} className="inline-block mt-8">
              <Button className="bg-brand-green hover:bg-brand-greendark text-white font-semibold h-12 px-7 gap-2">
                <Phone size={18} /> Call {siteSettings.phone}
              </Button>
            </a>
          </div>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-28 space-y-6">
            <div className="bg-brand-bluedeep rounded-2xl p-6 text-white">
              <h3 className="font-display font-bold text-xl mb-1">Serving {loc}</h3>
              <p className="text-white/70 text-sm mb-5">Fast, local, licensed & insured HVAC service.</p>
              <div className="flex items-center gap-3 mb-3">
                <Clock className="text-brand-green" size={20} />
                <span className="text-sm">{siteSettings.emergencyNote}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="text-brand-green" size={20} />
                <a href={`tel:${siteSettings.phoneRaw}`} className="font-display font-bold text-lg">{siteSettings.phone}</a>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-100">
              <iframe title="map" src={`https://www.google.com/maps?q=${encodeURIComponent(loc)}&output=embed`} className="w-full h-52 border-0" loading="lazy" />
            </div>
            <div className="flex flex-wrap gap-2">
              {serviceAreas.filter((a) => a.slug !== slug).map((a) => (
                <Link key={a.slug} to={`/service-areas/${a.slug}`} className="text-sm font-medium text-brand-blue bg-brand-bluelight rounded-full px-4 py-2 hover:bg-brand-blue hover:text-white transition-colors">
                  {a.state ? `${a.city}, ${a.state}` : a.city}
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <section className="py-16 bg-brand-bluelight">
        <div className="container-c max-w-3xl">
          <h2 className="font-display font-extrabold text-3xl text-brand-bluedark text-center">Request Service in {area.city}</h2>
          <p className="text-slate-600 text-center mt-3 mb-8">Tell us what you need and our local team will reach out right away.</p>
          <ContactForm />
        </div>
      </section>
    </div>
  );
};

export default ServiceArea;
